import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Plus, X, Users, FileText, ListChecks, Share2, HandHeart, CalendarClock, LifeBuoy } from "lucide-react";

/**
 * Floating quick-add button. Renders on every authenticated page (except /login
 * and /impact). Mobile-first: bottom-right, above the iOS safe-area inset.
 * Tap to expand a small menu of context-creating shortcuts.
 */
export default function QuickAddFab() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  // Close on outside click / Esc
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Hide on routes that don't need it (after all hooks have been called)
  const hiddenPaths = ["/login", "/impact"];
  if (hiddenPaths.some((p) => location.pathname.startsWith(p))) return null;

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const openHelpBeacon = () => {
    setOpen(false);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("help-beacon:open"));
    }
  };

  const onTechSupport = location.pathname.startsWith("/tech-support");
  const actions = [
    can?.("care_seekers.create") && { icon: Users, label: "Add Care Seeker", to: "/care-seekers?new=1", testId: "quick-care-seeker" },
    can?.("appointments.create") && { icon: CalendarClock, label: "Schedule appointment", to: "/appointments?new=1", testId: "quick-appointment" },
    can?.("notes.create") && { icon: FileText, label: "New wellness note", to: "/notes?new=1", testId: "quick-note" },
    can?.("tasks.create") && { icon: ListChecks, label: "New task", to: "/tasks?new=1", testId: "quick-task" },
    can?.("referrals.create") && { icon: Share2, label: "New referral", to: "/referrals?new=1", testId: "quick-referral" },
    can?.("outreach.create") && { icon: HandHeart, label: "Log outreach", to: "/outreach?new=1", testId: "quick-outreach" },
    !onTechSupport && can?.("tickets.create") && { icon: LifeBuoy, label: "Need help?", onClick: openHelpBeacon, testId: "quick-help" },
  ].filter(Boolean);

  if (actions.length === 0) return null;

  return (
    <div
      ref={menuRef}
      data-testid="quick-add-fab"
      className="fixed bottom-24 right-4 sm:bottom-20 sm:right-6 z-40 flex flex-col items-end gap-2"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
    >
      {open && (
        <div className="flex flex-col items-end gap-2 mb-1" data-testid="quick-add-menu">
          {actions.map((a) => (
            <button
              key={a.testId}
              onClick={() => (a.onClick ? a.onClick() : go(a.to))}
              data-testid={a.testId}
              className="group inline-flex items-center gap-3 bg-white border border-[#E5E5E0] rounded-full pl-4 pr-2 py-2 shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
            >
              <span className="text-sm font-medium text-[#0F172A] whitespace-nowrap">{a.label}</span>
              <span className="w-9 h-9 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center group-hover:bg-[#172554] transition-colors">
                <a.icon className="w-4 h-4" strokeWidth={2} />
              </span>
            </button>
          ))}
        </div>
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="quick-add-toggle"
        aria-label={open ? "Close quick add menu" : "Open quick add menu"}
        className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-105 ${
          open
            ? "bg-white text-[#1E3A8A] border-2 border-[#1E3A8A] rotate-45"
            : "bg-[#1E3A8A] text-white hover:bg-[#172554]"
        }`}
      >
        {open ? <X className="w-6 h-6" strokeWidth={2} /> : <Plus className="w-6 h-6" strokeWidth={2.5} />}
      </button>
    </div>
  );
}
